import { useCallback, useEffect, useRef, useState } from 'react';

export default function useDisplayWakeLock(enabled) {
  const [status, setStatus] = useState('idle');
  const lock = useRef(null);
  const pending = useRef(null);
  const recoveryTimer = useRef(null);
  const active = useRef(false);
  const generation = useRef(0);
  const request = useCallback(async () => {
    if (!active.current || pending.current || document.visibilityState === 'hidden') return;
    if (lock.current && !lock.current.released) return;
    lock.current = null;
    if (!navigator.wakeLock?.request) { setStatus('unsupported'); return; }
    window.clearTimeout(recoveryTimer.current);
    recoveryTimer.current = null;
    const attempt = {};
    pending.current = attempt;
    const current = generation.current;
    try {
      const sentinel = await navigator.wakeLock.request('screen');
      if (!active.current || generation.current !== current) { await sentinel.release(); return; }
      if (document.visibilityState === 'hidden' || sentinel.released) {
        setStatus('released');
        await sentinel.release();
        return;
      }
      lock.current = sentinel; setStatus('active');
      sentinel.addEventListener('release', () => {
        if (lock.current !== sentinel) return;
        lock.current = null;
        if (!active.current || generation.current !== current) return;
        setStatus('released');
        if (document.visibilityState !== 'hidden') {
          // Recover promptly, but avoid a tight loop if the device keeps revoking locks.
          recoveryTimer.current = window.setTimeout(() => {
            recoveryTimer.current = null;
            request();
          }, 1000);
        }
      });
    } catch {
      if (active.current && generation.current === current) setStatus('unavailable');
    } finally {
      if (pending.current === attempt) pending.current = null;
    }
  }, []);
  useEffect(() => {
    active.current = enabled;
    if (!enabled) { setStatus('idle'); return undefined; }
    request();
    document.addEventListener('visibilitychange', request);
    document.addEventListener('fullscreenchange', request);
    document.addEventListener('keydown', request);
    document.addEventListener('pointerdown', request);
    window.addEventListener('focus', request);
    window.addEventListener('pageshow', request);
    const retry = window.setInterval(request, 30000);
    return () => {
      active.current = false; generation.current += 1;
      pending.current = null;
      document.removeEventListener('visibilitychange', request);
      document.removeEventListener('fullscreenchange', request);
      document.removeEventListener('keydown', request);
      document.removeEventListener('pointerdown', request);
      window.removeEventListener('focus', request);
      window.removeEventListener('pageshow', request);
      window.clearInterval(retry);
      window.clearTimeout(recoveryTimer.current);
      recoveryTimer.current = null;
      const sentinel = lock.current; lock.current = null;
      sentinel?.release().catch(() => {});
    };
  }, [enabled, request]);
  return { status, request };
}
